import { useAbilityRoute } from "@/shared/environment/ability-route";
import { decodeParamArrayFromString, encodeParamArrayToString } from "@/shared/environment/ability-route-helper";
import { CommonRouteAliases } from "@/shared/environment/common-route-aliases";
import { MechanicPath } from "@/shared/services/graphql/generated/consumer-graph-types";
import { computed } from "vue";
import { useRoute } from "vue-router";

export type MechanicContext = CommonRouteAliases.mechanicCombinedView | CommonRouteAliases.mechanic;

export const useMechanicRoute = () => {
    const { getPathToChildrenWithoutParents, getPathToChild } = useAbilityRoute();
    const route = useRoute();

    /**
     * Detects whether the current route is under 'mechanic' or 'combinedview'
     * @returns The current mechanic context alias (mechanic or combinedview)
     */
    const getCurrentMechanicContext = (): MechanicContext => {
        if (route.params[CommonRouteAliases.mechanicCombinedView] !== undefined) {
            return CommonRouteAliases.mechanicCombinedView;
        }
        return CommonRouteAliases.mechanic;
    };

    const isCombinedView = computed(() => {
        return getCurrentMechanicContext() === CommonRouteAliases.mechanicCombinedView;
    });

    const getAbsolutePathToMechanic = (
        product: string,
        asset: string | undefined,
        assemblyPath: Array<string>,
        mechanicArticle: string | undefined,
        context?: MechanicContext
    ) => {
        const contextAlias = context ?? getCurrentMechanicContext();

        return getPathToChildrenWithoutParents([
            {
                alias: CommonRouteAliases.product,
                namedParams: { [CommonRouteAliases.product]: product },
            },
            ...(asset
                ? [
                      {
                          alias: CommonRouteAliases.asset,
                          namedParams: { [CommonRouteAliases.asset]: asset },
                      },
                  ]
                : []),
            {
                alias: contextAlias,
                namedParams: { [contextAlias]: encodeParamArrayToString(assemblyPath) },
            },
            ...(mechanicArticle
                ? [
                      {
                          alias: CommonRouteAliases.mechanicArticle,
                          namedParams: { [CommonRouteAliases.mechanicArticle]: mechanicArticle },
                      },
                  ]
                : []),
        ]);
    };

    const getAbsolutePathToMechanicLocation = (
        product: string,
        asset: string | undefined,
        mechanicPath: MechanicPath,
        context?: MechanicContext
    ) => {
        function getAssemblyPath(): Array<string> {
            const result: Array<string> = [];
            mechanicPath.path?.forEach((item) => {
                if (item?.mechanic?.assemblyId) {
                    result.push(item?.mechanic?.assemblyId);
                }
            });
            // Letzter Eintrag ist der Stücklisteneintrag selbst und gehört nicht zur Liste
            if (result.length > 0) {
                result.pop();
            }
            //Add root if not already in path
            if (result[0] !== "") {
                result.unshift("");
            }
            return result;
        }

        function getMechanicArticleFromPath(): string | undefined {
            const len = mechanicPath.path?.length ?? 0;
            if (len > 0) {
                const lastElement = mechanicPath.path?.[len - 1];
                return lastElement?.mechanicId ?? undefined;
            } else {
                return undefined;
            }
        }

        return getAbsolutePathToMechanic(product, asset, getAssemblyPath(), getMechanicArticleFromPath(), context);
    };

    /**
     * Context-aware version of getPathToChild that automatically uses the correct alias
     * @param namedParams Route parameters
     * @returns Route path
     */
    const getContextualPathToChild = (namedParams: Record<string, string>) => {
        const contextAlias = getCurrentMechanicContext();
        return getPathToChild(contextAlias, namedParams);
    };

    function getAssembliesFromCurrentRouteContext() {
        const contextAlias = getCurrentMechanicContext();
        const params = route.params[contextAlias] as string;
        const assemblies = decodeParamArrayFromString(params ?? "");
        return assemblies;
    }

    function getContextualPathToSubAssembly(assemblyId: string) {
        const contextAlias = getCurrentMechanicContext();
        const assemblies = getAssembliesFromCurrentRouteContext();
        assemblies.push(assemblyId);

        return getContextualPathToChild({
            [contextAlias]: encodeParamArrayToString(assemblies),
        });
    }

    return {
        isCombinedView,
        getAbsolutePathToMechanic,
        getAbsolutePathToMechanicLocation,
        getCurrentMechanicContext,
        getContextualPathToChild,
        getAssembliesFromCurrentRouteContext,
        getContextualPathToSubAssembly,
    };
};
